import { useState } from 'react';

/* ─── Design tokens ────────────────────────────────────────────────────────── */
const C = {
  coral:  'var(--primary-color)',
  bg:     'var(--bg-dark)',
  surface:'var(--bg-card)',
  border: 'var(--border-color)',
  text:   'var(--text-primary)',
  sub:    'var(--text-secondary)',
  mute:   'var(--text-mute)',
};

/* ─── Data ─────────────────────────────────────────────────────────────────── */
const STEPS = [
  {
    num: '01',
    title: 'Indsæt dit RSS-feed',
    body:  'Én URL. Vi henter titel, artwork og episodeliste automatisk. Virker med alle RSS-værter — Podbean, Buzzsprout, Transistor, selvhostede.',
  },
  {
    num: '02',
    title: 'Verificer ejerskab',
    body:  'En engangskode til den e-mail der er tilknyttet dit feed — eller et lille verifikations-tag. Tager under fem minutter.',
  },
  {
    num: '03',
    title: 'Du er live',
    body:  'Dit podcast er nu synligt i Faircast-appen. Alle eksisterende episoder er inkluderet fra starten. Dine nuværende lyttere mærker ingenting.',
  },
  {
    num: '04',
    title: 'Penge i banken',
    body:  'Sidst på måneden beregner vi din andel ud fra faktisk lyttetid. Inden d. 5. i næste måned er de på din konto.',
  },
];

const COMPARE = [
  { platform: 'Faircast',           cut: '20%',   model: 'Lyttetid (præcis)',   ads: 'Nej', lock: 'Nej', dk: 'Ja'  },
  { platform: 'Spotify (Excl.)',    cut: '~50%+', model: 'Streams (min. 1.000)',ads: 'Ja',  lock: 'Eksklusiv', dk: 'Nej' },
  { platform: 'Patreon',            cut: '8-12%', model: 'Frivillig støtte',    ads: 'Nej', lock: 'Nej', dk: 'Nej' },
  { platform: 'Apple Podcasts',     cut: '30%',   model: 'Pr. abonnent',        ads: 'Nej', lock: 'Nej', dk: 'Nej' },
];

const FAQ_ITEMS = [
  {
    q: 'Hvad koster det at tilmelde mit podcast?',
    a: 'Ingenting up front. Vi tager 20% af det du optjener — ingen opret-gebyr, ingen månedlig fast ydelse, ingen minimumskrav til følgere eller downloads. Tjener du ingenting, betaler du ingenting.',
  },
  {
    q: 'Hvad sker der med mit RSS-feed og mine nuværende lyttere?',
    a: 'Ingenting ændrer sig for dem. Du beholder dit feed og alle eksisterende kanaler — Spotify, Apple Podcasts, alle andre. Faircast er en ekstra indkomstkanal, ikke en erstatning. Dine nuværende lyttere mærker ingenting.',
  },
  {
    q: 'Hvornår udbetales pengene?',
    a: 'Sidst på måneden opgøres alle lytteres faktiske lyttetid og fordeles automatisk. Pengene rammer din konto inden den 5. i den efterfølgende måned. Ingen minimumsudbetaling.',
  },
  {
    q: 'Hvad tæller som lyttetid?',
    a: 'Kun aktivt afspilning. Pauser lytteren, stopper tællingen. Spoler de frem, tæller kun det de faktisk hørte. Ingen "én afspilning = fuld episode"-tricks. Det er fair — for begge parter.',
  },
  {
    q: 'Kan jeg se statistik på mine lyttere?',
    a: 'Ja — anonymiseret. Du ser antal lyttere, gennemsnitlig lytteandel, top-episoder og din månedlige optjening opgjort per episode. Vi deler ikke personoplysninger om individuelle lyttere.',
  },
  {
    q: 'Hvad med moms og skat?',
    a: 'Du modtager bruttobeløbet og er selv ansvarlig for korrekt momsangivelse og skatteindberetning som ved enhver anden selvstændig indkomst. Vi sender en årsopgørelse i januar med alle detaljer.',
  },
  {
    q: 'Kan jeg stadig have annoncer i mit podcast?',
    a: 'Ja. Vi kræver ikke eksklusivitet og dikterer ikke dit indhold. Faircast-lyttere hører hvad du uploader — inklusiv eventuelle annoncer, sponsorater og reklamepauseindlæg. Det er dit show.',
  },
];

/* ─── Sub-components ───────────────────────────────────────────────────────── */
function FaqItem({ q, a, open, onToggle }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 0', textAlign: 'left', gap: 16,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 15, color: C.text, lineHeight: 1.4 }}>{q}</span>
        <span style={{
          fontSize: 22, color: C.coral, flexShrink: 0,
          transform: open ? 'rotate(45deg)' : 'none',
          transition: 'transform 0.15s ease',
          display: 'inline-block', lineHeight: 1,
        }}>+</span>
      </button>
      {open && (
        <p style={{ fontSize: 14, lineHeight: 1.75, color: C.sub, paddingBottom: 20, margin: 0 }}>
          {a}
        </p>
      )}
    </div>
  );
}

/* ─── Component ────────────────────────────────────────────────────────────── */
function ForCreators() {
  const [listeners, setListeners] = useState(500);
  const [avgPct, setAvgPct]       = useState(35);
  const [openFaq, setOpenFaq]     = useState(null);

  const monthly     = listeners * 24 * (avgPct / 100);
  const perListener = listeners > 0 ? monthly / listeners : 0;

  const fmtKr = n =>
    n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section style={{ padding: '80px 0 64px' }}>
        <div className="fc-wrap">
          <span className="fc-eyebrow">For skabere</span>
          <h1 style={{
            fontSize: 'clamp(36px, 6vw, 68px)',
            fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.04,
            color: C.text, marginBottom: 24, maxWidth: 760,
          }}>
            Dine lyttere betaler.<br />
            <em style={{ fontStyle: 'normal', color: C.coral }}>Du modtager 80%.</em>
          </h1>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: C.sub, maxWidth: 560, marginBottom: 20 }}>
            Faircast er den eneste danske podcast-platform hvor du kender formlen på forhånd — og hvor den aldrig ændrer sig.
          </p>
          {/* Formula pill */}
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 14, color: C.coral,
            background: 'rgba(255,107,53,0.08)',
            border: '1px solid rgba(255,107,53,0.25)',
            borderRadius: 10, padding: '10px 18px',
            display: 'inline-block', marginBottom: 40,
          }}>
            optjening = lyttere × 24 kr × lytteandel
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a href="/login" className="fc-cta-primary">Opret skaber-konto gratis</a>
            <a href="#calculator" className="fc-cta-ghost">Beregn din optjening ↓</a>
          </div>
        </div>
      </section>

      {/* ── The math ────────────────────────────────────────────────────── */}
      <section style={{ padding: '64px 0', background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="fc-wrap">
          <div className="fc-grid-2 fc-grid-2--center">
            <div>
              <span className="fc-eyebrow">Formlen</span>
              <h2 className="fc-section-title">Ingen mystik.<br />Bare matematik.</h2>
              <p className="fc-section-sub">
                Hver Faircast-lytter betaler 30 kr/md. 80% — 24 kr — går til de skabere de lytter til, fordelt præcist efter faktisk lyttetid. Vi tager 20 % til drift. Det er hele aftalen.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: C.sub, marginTop: 16 }}>
                Bruger en lytter 40% af sin samlede lyttetid på dit podcast, modtager du 40% af deres 24 kr — uanset om de lyttede i 20 minutter eller 5 timer.
              </p>
            </div>

            {/* Breakdown card */}
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 20, padding: 28 }}>
              {[
                { label: 'Abonnementspris',          value: '30,00 kr', note: 'betalt af lytteren månedligt'    },
                { label: '− Platform (20 %)',         value: '− 6,00 kr', note: 'Faircast drift'                 },
                { label: '= Skaber-pulje pr. lytter', value: '24,00 kr', note: 'fordeles sidst på måneden', bold: true },
                { label: '× Din lytteandel (eks.)',   value: '35 %',     note: 'gennemsnit på tværs af abonnenter' },
                { label: '= Du modtager',             value: '8,40 kr',  note: 'pr. lytter pr. måned', coral: true   },
              ].map((row, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '11px 0', borderBottom: i < 4 ? `1px solid ${C.border}` : 'none', gap: 16,
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: row.bold || row.coral ? 700 : 400, color: row.coral ? C.coral : row.bold ? C.text : C.sub }}>
                      {row.label}
                    </div>
                    <div style={{ fontSize: 12, color: C.mute, marginTop: 2 }}>{row.note}</div>
                  </div>
                  <div style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 15, fontWeight: 700,
                    color: row.coral ? C.coral : C.text,
                    flexShrink: 0,
                  }}>{row.value}</div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ── How to start ────────────────────────────────────────────────── */}
      <section style={{ padding: '64px 0' }}>
        <div className="fc-wrap">
          <span className="fc-eyebrow">Kom i gang</span>
          <h2 className="fc-section-title" style={{ marginBottom: 8 }}>Fire trin. Ingen venteliste.</h2>
          <p className="fc-section-sub" style={{ marginBottom: 48 }}>
            Fra RSS-URL til live på platformen på under ti minutter.
          </p>
          <div className="fc-grid-4">
            {STEPS.map(step => (
              <div key={step.num} style={{
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: '28px 24px',
              }}>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, fontWeight: 700, color: C.coral,
                  letterSpacing: '0.08em', marginBottom: 16,
                }}>{step.num}</div>
                <h3 style={{ fontWeight: 700, color: C.text, marginBottom: 10, fontSize: 15, lineHeight: 1.35 }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: C.sub, lineHeight: 1.65, margin: 0 }}>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Calculator ──────────────────────────────────────────────────── */}
      <section id="calculator" style={{ padding: '64px 0', background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="fc-wrap">
          <span className="fc-eyebrow">Regneeksempel</span>
          <h2 className="fc-section-title" style={{ marginBottom: 8 }}>Hvad kan du tjene?</h2>
          <p className="fc-section-sub" style={{ marginBottom: 48 }}>
            Justér tallene nedenfor. Din faktiske optjening afhænger af dine lytteres adfærd — men formlen er altid den samme.
          </p>

          <div className="fc-grid-2">

            {/* Sliders */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <label style={{ fontSize: 14, color: C.sub, fontWeight: 600 }}>Antal Faircast-lyttere</label>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 700, color: C.text }}>
                    {listeners.toLocaleString('da-DK')}
                  </span>
                </div>
                <input
                  type="range" min="10" max="10000" step="10"
                  value={listeners}
                  onChange={e => setListeners(Number(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary-color)' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.mute, marginTop: 6 }}>
                  <span>10</span><span>10.000</span>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <label style={{ fontSize: 14, color: C.sub, fontWeight: 600 }}>Gennemsnitlig lytteandel</label>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 700, color: C.text }}>
                    {avgPct} %
                  </span>
                </div>
                <input
                  type="range" min="1" max="90" step="1"
                  value={avgPct}
                  onChange={e => setAvgPct(Number(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--primary-color)' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.mute, marginTop: 6 }}>
                  <span>1 % (ét show blandt mange)</span><span>90 % (dominerende)</span>
                </div>
              </div>

              <div style={{
                background: C.bg, border: `1px solid ${C.border}`,
                borderRadius: 14, padding: '16px 20px',
                fontSize: 13, color: C.mute, lineHeight: 1.65,
              }}>
                <strong style={{ color: C.sub, display: 'block', marginBottom: 6 }}>Hvad er lytteandel?</strong>
                Andelen af en lytters samlede månedlige lyttetid, de bruger på dit show. Har du et nichepublikum der returnerer ofte, er den høj. Er du ét af mange shows, er den lavere.
              </div>
            </div>

            {/* Result card */}
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 20, padding: 28 }}>
              <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '1px dashed var(--border-strong)' }}>
                <div style={{ fontSize: 11, color: C.mute, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  Estimeret månedlig optjening
                </div>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 44, fontWeight: 800, color: C.coral, lineHeight: 1,
                }}>
                  {fmtKr(monthly)} kr
                </div>
                <div style={{ fontSize: 13, color: C.mute, marginTop: 8 }}>
                  {listeners.toLocaleString('da-DK')} lyttere · {avgPct} % andel · 24 kr skaber-pulje
                </div>
              </div>

              {[
                { label: 'Lyttere',                   value: listeners.toLocaleString('da-DK') },
                { label: 'Skaber-pulje pr. lytter',   value: '24,00 kr'                        },
                { label: 'Din lytteandel (gns.)',      value: `${avgPct} %`                     },
                { label: 'Din optjening pr. lytter',   value: `${fmtKr(perListener)} kr`        },
                { label: 'Din månedlige optjening',    value: `${fmtKr(monthly)} kr`, coral: true },
              ].map((row, i) => (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '9px 0', fontSize: 14,
                  borderBottom: i < 4 ? `1px solid ${C.border}` : 'none',
                }}>
                  <span style={{ color: C.sub }}>{row.label}</span>
                  <span style={{
                    fontWeight: 700, color: row.coral ? C.coral : C.text,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{row.value}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ── Why Faircast (comparison table) ─────────────────────────────── */}
      <section style={{ padding: '64px 0' }}>
        <div className="fc-wrap">
          <span className="fc-eyebrow">Sammenlignet</span>
          <h2 className="fc-section-title" style={{ marginBottom: 8 }}>Hvad du ikke finder andre steder</h2>
          <p className="fc-section-sub" style={{ marginBottom: 48 }}>En ligetil sammenligning. Ingen spin.</p>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  {['Platform', 'Platform-andel', 'Betalingsmodel', 'Annoncer', 'Binding', 'Dansk'].map(h => (
                    <th key={h} style={{
                      textAlign: 'left', padding: '12px 16px',
                      color: C.mute, fontWeight: 600, fontSize: 12,
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                      borderBottom: `1px solid ${C.border}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr key={i} style={{ background: i === 0 ? 'rgba(255,107,53,0.06)' : 'transparent' }}>
                    <td style={{ padding: '14px 16px', fontWeight: i === 0 ? 700 : 400, color: i === 0 ? C.coral : C.text, borderBottom: `1px solid ${C.border}` }}>{row.platform}</td>
                    <td style={{ padding: '14px 16px', fontFamily: "'JetBrains Mono', monospace", color: i === 0 ? C.text : C.sub, borderBottom: `1px solid ${C.border}` }}>{row.cut}</td>
                    <td style={{ padding: '14px 16px', color: C.sub, borderBottom: `1px solid ${C.border}` }}>{row.model}</td>
                    <td style={{ padding: '14px 16px', fontWeight: 600, color: row.ads === 'Nej' ? '#4FD97F' : '#FF6B6B', borderBottom: `1px solid ${C.border}` }}>{row.ads}</td>
                    <td style={{ padding: '14px 16px', fontWeight: 600, color: row.lock === 'Nej' ? '#4FD97F' : '#FF6B6B', borderBottom: `1px solid ${C.border}` }}>{row.lock}</td>
                    <td style={{ padding: '14px 16px', fontWeight: row.dk === 'Ja' ? 600 : 400, color: row.dk === 'Ja' ? '#4FD97F' : C.mute, borderBottom: `1px solid ${C.border}` }}>{row.dk}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: 14, fontSize: 13, color: C.mute }}>
            * Spotify-andel afhænger af aftalemodel og er ikke officielt publiceret. Estimat baseret på tilgængelige oplysninger.
          </p>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section style={{ padding: '64px 0', background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="fc-wrap">
          <span className="fc-eyebrow">Spørgsmål</span>
          <h2 className="fc-section-title" style={{ marginBottom: 48 }}>Hvad skabere spørger om</h2>
          <div style={{ maxWidth: 740 }}>
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem
                key={i}
                q={item.q}
                a={item.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section style={{ padding: '80px 0' }}>
        <div className="fc-wrap" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(24px, 4vw, 44px)',
            fontWeight: 800, letterSpacing: '-0.03em',
            color: C.text, marginBottom: 16,
          }}>
            Klar til et{' '}
            <em style={{ fontStyle: 'normal', color: C.coral }}>fair</em>
            {' '}honorar?
          </h2>
          <p style={{
            fontSize: 17, color: C.sub, lineHeight: 1.6,
            maxWidth: 480, margin: '0 auto 32px',
          }}>
            Gratis at tilmelde. Ingen binding. Du beholder dit RSS-feed og dine eksisterende kanaler fuldt ud.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <a href="/login" className="fc-cta-primary">Opret skaber-konto</a>
            <a href="/about" className="fc-cta-ghost">Hvem er bag Faircast?</a>
          </div>
          <p style={{ fontSize: 13, color: C.mute }}>Ingen binding · Gratis oprettelse · Opsig hvornår du vil</p>
        </div>
      </section>

    </div>
  );
}

export default ForCreators;
